import React, { useState } from 'react';
import { Trash2, Timer, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInDays } from 'date-fns';

const API = '/api';

const PaymentsTab = ({ leads, payments, fetchData }) => {
  const [triggeringCountdown, setTriggeringCountdown] = useState(null);

  const handleRecordPayment = async (leadId) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API}/payments`,
        { lead_id: leadId, amount: 149 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment recorded successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleTriggerCountdown = async (payment) => {
    const token = localStorage.getItem('admin_token');
    setTriggeringCountdown(payment.id);
    
    try {
      // Calculate days remaining until refund deadline
      const daysRemaining = differenceInDays(new Date(payment.refund_deadline), new Date());
      
      // Send appropriate countdown email based on days remaining
      let emailType = 'countdown_30';
      if (daysRemaining <= 15) {
        emailType = 'countdown_15';
      }
      
      await axios.post(
        `${API}/emails/resend?email=${encodeURIComponent(payment.lead_email)}&email_type=${emailType}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`30-day countdown reminder sent to ${payment.lead_email}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send countdown email');
    } finally {
      setTriggeringCountdown(null);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-4">Payment Management</h3>
      <div className="mb-6">
        <h4 className="text-lg font-semibold mb-4">Record New Payment</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.filter(l => l.status === 'completed' && !payments.find(p => p.lead_id === l.id)).map((lead) => (
            <div key={lead.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="font-semibold mb-1">{lead.name}</p>
              <p className="text-sm text-[#525252] mb-3">{lead.email}</p>
              <button
                onClick={() => handleRecordPayment(lead.id)}
                className="w-full bg-[#FF4500] text-white px-4 py-2 rounded-lg hover:bg-[#E03E00] transition-all duration-300 text-sm font-semibold"
                data-testid={`record-payment-${lead.id}`}
              >
                Record $149 Payment
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <h4 className="text-lg font-semibold mb-4">Payment History</h4>
        
        {/* Countdown Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Timer className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h5 className="font-semibold text-blue-800 mb-1">30-Day Refund Countdown</h5>
              <p className="text-sm text-blue-700">
                Use the "Start Countdown" button to send refund reminder emails to clients after receiving payment from a Next Steps Call.
                The system will automatically send the appropriate reminder (15-day or 30-day) based on their refund deadline.
              </p>
            </div>
          </div>
        </div>
        
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Lead Name</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Amount</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Date</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Refund Deadline</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Days Left</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Status</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-[#0A0A0A]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div>
                    <p className="text-sm font-semibold">{payment.lead_name || 'Unknown'}</p>
                    <p className="text-xs text-[#525252]">{payment.lead_email || ''}</p>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm font-semibold text-[#25D366]">${payment.amount}</td>
                <td className="py-3 px-4 text-sm">{format(new Date(payment.date), 'MMM d, yyyy')}</td>
                <td className="py-3 px-4 text-sm">{format(new Date(payment.refund_deadline), 'MMM d, yyyy')}</td>
                <td className="py-3 px-4">
                  {(() => {
                    const daysLeft = differenceInDays(new Date(payment.refund_deadline), new Date());
                    if (daysLeft < 0) {
                      return <span className="text-xs font-semibold text-gray-500">Expired</span>;
                    } else if (daysLeft <= 7) {
                      return <span className="text-xs font-semibold text-red-600">{daysLeft} days</span>;
                    } else if (daysLeft <= 15) {
                      return <span className="text-xs font-semibold text-amber-600">{daysLeft} days</span>;
                    } else {
                      return <span className="text-xs font-semibold text-green-600">{daysLeft} days</span>;
                    }
                  })()}
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                    payment.status === 'active' ? 'bg-[#25D366] text-white' : 'bg-gray-200 text-[#0A0A0A]'
                  }`}>
                    {payment.status}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {/* Start Countdown Button */}
                    <button
                      onClick={() => handleTriggerCountdown(payment)}
                      disabled={triggeringCountdown === payment.id}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200 disabled:opacity-50"
                      title="Send 30-day countdown reminder"
                      data-testid={`trigger-countdown-${payment.id}`}
                    >
                      {triggeringCountdown === payment.id ? (
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </button>
                    
                    {/* Delete Button */}
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Delete payment for "${payment.lead_name}"?`)) return;
                        const token = localStorage.getItem('admin_token');
                        try {
                          await axios.delete(`${API}/payments/${payment.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Payment deleted');
                          fetchData();
                        } catch (error) {
                          toast.error('Failed to delete payment');
                        }
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete payment"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments.length === 0 && (
          <p className="text-center text-[#525252] py-8">No payments yet</p>
        )}
      </div>
    </div>
  );
};

export default PaymentsTab;
